<template>
  <!-- Календарь месяца на вкладке «аналитика». Нажатие на день открывает его;
       выбранный период — сплошная затемняющая полоса, крайние дни обведены
       (ручек нет: их не протянуть в соседний месяц — решение владельца
       27.10.2026). В режиме выбора (pickMode) полосы нет, нажатие на день
       отдаёт его наверх как начало или конец. Месяцы листаются стрелками и
       свайпом: лента из трёх месяцев едет за пальцем, отпустил раньше трети
       ширины — вернулась (как календарь Apple). Легенда зависит от настройки
       цветового кодирования дефицита и профицита. -->
  <div class="px-3 pt-2 pb-1 select-none">
    <!-- Заголовок месяца с навигацией -->
    <div class="flex items-center justify-center gap-4 mt-2 mb-3">
      <button @click="slide(-1)" class="w-8 h-8 flex items-center justify-center rounded-full text-ink active:bg-line transition-colors" aria-label="Предыдущий месяц">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>
      <p data-guide="cal-month" class="text-16 font-semibold text-ink min-w-[140px] text-center" style="font-family: Inter, sans-serif;">
        {{ monthLabel }}
      </p>
      <button
        @click="slide(1)"
        :disabled="isLastMonth"
        class="w-8 h-8 flex items-center justify-center rounded-full text-ink active:bg-line transition-colors disabled:opacity-20"
        aria-label="Следующий месяц"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>
    </div>

    <!-- Заголовки дней -->
    <div class="grid grid-cols-7 mb-1" :style="{ gap: GAP + 'px' }">
      <div v-for="wd in WEEKDAYS" :key="wd" class="text-center text-13 text-ink/70" style="font-family: Inter, sans-serif;">
        {{ wd }}
      </div>
    </div>

    <!-- Лента из трёх месяцев: предыдущий, текущий, следующий -->
    <div ref="wrapEl" data-guide="cal-grid" class="overflow-hidden" style="touch-action: pan-y;"
         @touchstart.passive="onTouchStart" @touchmove.passive="onTouchMove" @touchend="onTouchEnd" @touchcancel="onTouchEnd">
      <!-- Квадрат дня считается от ширины экрана: зазор между квадратами 6 px,
           как между карточками блюд в приёме (MealGroup.vue); на широком
           телефоне квадраты крупнее, а не расстояния (владелец 28.10.2026).
           Сетка всегда на 6 строк, чтобы высота не прыгала между месяцами -->
      <div class="flex" :style="stripStyle" @transitionend="onTransitionEnd">
        <div v-for="(m, mi) in strip" :key="m.key" class="grid grid-cols-7 shrink-0" :style="gridStyle(m)" :data-guide="mi === 1 ? 'cal-days' : null">
          <div v-for="n in m.offset" :key="'empty-' + n" />
          <button
            v-for="day in m.days"
            :key="day.date"
            :data-date="day.date"
            @click="onDayClick(day)"
            class="cell flex items-center justify-center transition-colors"
            :class="{ 'in-band': day.inBand, 'band-start': day.bandStart, 'band-end': day.bandEnd }"
          >
            <span
              class="w-full h-full flex items-center justify-center rounded-xl text-18 text-ink"
              :style="bgStyle(day.date, day.hasEntries)"
              :class="[
                selectedDate === day.date ? 'bg-ink/10' : '',
                day.isToday ? 'font-bold' : 'font-light',
                (day.isFuture && !day.isPlanned) || day.notPickable ? 'opacity-30' : '',
                day.isPlanned ? 'day-planned' : '',
                day.isEdge || day.isPickStart ? 'day-edge' : '',
              ]"
              style="font-family: Inter, sans-serif;"
            >
              {{ day.dayNum }}
            </span>
            <small v-if="day.isToday" class="today-label">сегодня</small>
          </button>
        </div>
      </div>
    </div>
    <ul class="calendar-legend" aria-label="Обозначения дней">
      <template v-if="showKcalColors">
        <li><i :style="{ background: KCAL_DAY_COLORS.deficit }" aria-hidden="true" />дефицит</li>
        <li><i :style="{ background: KCAL_DAY_COLORS.maintenance }" aria-hidden="true" />норма</li>
        <li><i :style="{ background: KCAL_DAY_COLORS.surplus }" aria-hidden="true" />профицит</li>
      </template>
      <li v-else><i :style="{ background: KCAL_DAY_COLORS.maintenance }" aria-hidden="true" />полный день</li>
      <li><i class="incomplete-swatch" aria-hidden="true" /><span>неполный<span class="legend-day-word"> день</span></span></li>
    </ul>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { dayBackground, KCAL_DAY_COLORS } from '../utils/kcalDayColor.js'

const props = defineProps({
  dayStates: { type: Object, default: () => ({}) },
  daysWithEntries: { type: Array, default: () => [] },
  selectedDate: { type: String, default: null },
  dayKcals: { type: Object, default: () => ({}) },
  kcalNorm: { type: Number, default: 0 },
  showKcalColors: { type: Boolean, default: true },
  // Будущие дни, на которые уже что-то записано (повтор «на завтра / на
  // неделю») — GET /diary/summary, planned_days. Показываются пунктирной
  // рамкой, без цвета калорий: план — не съеденное.
  plannedDays: { type: Array, default: () => [] },
  // Выбранный период: сплошная полоса, крайние дни обведены
  rangeFrom: { type: String, default: null },
  rangeTo: { type: String, default: null },
  // Режим выбора дат: 'none' | 'start' | 'end'. В нём нажатие на день — pick, а не select
  pickMode: { type: String, default: 'none' },
  // Уже выбранное начало, пока ждём конец
  pickStart: { type: String, default: null },
  // Последний день, который можно выбрать (сегодня)
  maxDay: { type: String, default: null },
  // Показать месяц YYYY-MM (после выбора периода — месяц его конца)
  focusMonth: { type: String, default: null },
})

const emit = defineEmits(['select', 'pick', 'month-change'])

const WEEKDAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс']
const MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']

const today = new Date()
const currentYear = today.getFullYear()
const currentMonth = today.getMonth()

const viewYear = ref(currentYear)
const viewMonth = ref(currentMonth)

watch(() => props.focusMonth, (m) => {
  if (!m || !/^\d{4}-\d{2}$/.test(m)) return
  const y = Number(m.slice(0, 4)), mo = Number(m.slice(5, 7)) - 1
  if (y === viewYear.value && mo === viewMonth.value) return
  viewYear.value = y; viewMonth.value = mo
  emit('month-change', y, mo)
}, { immediate: true })
// Вперёд можно на один месяц: туда ложатся планы «на неделю вперёд»
// (migrations/add_entry_duplication.sql), дальше смотреть нечего
const MONTHS_AHEAD = 1
const isLastMonth = computed(() => (viewYear.value * 12 + viewMonth.value) >= (currentYear * 12 + currentMonth + MONTHS_AHEAD))

function shiftMonth(dir) {
  let m = viewMonth.value + dir, y = viewYear.value
  if (m < 0) { m = 11; y-- } else if (m > 11) { m = 0; y++ }
  viewMonth.value = m; viewYear.value = y
  emit('month-change', y, m)
}

// ── Размер квадрата дня от ширины: (ширина − 6 зазоров) / 7 ──
const GAP = 6
const TODAY_LABEL_SPACE = 14
const wrapEl = ref(null)
const cell = ref(46)
let ro = null
function measure() { if (wrapEl.value) cell.value = Math.floor((wrapEl.value.clientWidth - 6 * GAP) / 7) }
onMounted(() => {
  measure()
  if (typeof ResizeObserver !== 'undefined' && wrapEl.value) { ro = new ResizeObserver(measure); ro.observe(wrapEl.value) }
})
onUnmounted(() => { ro?.disconnect() })
// Высота — по строкам текущего месяца (5 или 6), меняется плавно при
// листании: пустая шестая строка отодвигала графики (владелец 28.10.2026)
const rowsOf = (m) => Math.ceil((m.offset + m.days.length) / 7)
function rowHeights(m) {
  const heights = Array(rowsOf(m)).fill(cell.value)
  const todayIndex = m.days.findIndex(d => d.isToday)
  if (todayIndex !== -1) heights[Math.floor((m.offset + todayIndex) / 7)] += TODAY_LABEL_SPACE
  return heights
}
function gridStyle(m) {
  const heights = rowHeights(strip.value[1])
  const height = heights.reduce((sum, h) => sum + h, 0) + (heights.length - 1) * GAP
  return {
    width: '33.3333%',
    gap: GAP + 'px',
    '--day-size': cell.value + 'px',
    gridTemplateRows: rowHeights(m).map(h => h + 'px').join(' '),
    minHeight: height + 'px',
    maxHeight: height + 'px',
    overflow: 'hidden',
    transition: 'min-height .28s, max-height .28s',
  }
}

// ── Плавное листание: смещение ленты и анимация к соседнему месяцу ──
const offset = ref(0)          // px, лента сдвинута за пальцем
const animating = ref(false)
let pendingDir = 0             // куда перелистнуть, когда анимация доедет
let fallbackTimer = null
const stripStyle = computed(() => ({
  width: '300%',
  transform: `translateX(calc(-33.3333% + ${offset.value}px))`,
  transition: animating.value ? 'transform .28s cubic-bezier(.2, .8, .2, 1)' : 'none',
}))
function width() { return wrapEl.value?.clientWidth || 320 }
/** Уехать на соседний месяц (dir −1 назад, 1 вперёд) или вернуться (0) */
function slide(dir) {
  if (animating.value) return
  if (dir > 0 && isLastMonth.value) dir = 0
  pendingDir = dir
  animating.value = true
  offset.value = -dir * width()
  // transitionend не придёт, если смещение не изменилось — страховка
  clearTimeout(fallbackTimer)
  fallbackTimer = setTimeout(onTransitionEnd, 360)
}
function onTransitionEnd() {
  if (!animating.value) return
  clearTimeout(fallbackTimer)
  animating.value = false
  if (pendingDir) shiftMonth(pendingDir)
  pendingDir = 0
  offset.value = 0   // новый текущий месяц уже в середине ленты — без перехода
}

// ── Свайп: горизонтальный жест ведёт ленту, вертикальный отдан прокрутке ──
let touch = null
function onTouchStart(e) {
  if (animating.value) return
  const t = e.touches[0]
  touch = { x: t.clientX, y: t.clientY, axis: null, t0: Date.now() }
}
function onTouchMove(e) {
  if (!touch) return
  const t = e.touches[0]
  const dx = t.clientX - touch.x, dy = t.clientY - touch.y
  if (!touch.axis) {
    if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
    touch.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'
  }
  if (touch.axis !== 'x') return
  // Вперёд дальше последнего месяца нельзя — лента упирается с сопротивлением
  offset.value = dx < 0 && isLastMonth.value ? dx / 4 : dx
}
function onTouchEnd() {
  if (!touch) return
  const { axis, t0 } = touch
  touch = null
  if (axis !== 'x') return
  const dx = offset.value
  const fast = Math.abs(dx) > 24 && Date.now() - t0 < 250
  const far = Math.abs(dx) > width() / 3
  slide(far || fast ? (dx < 0 ? 1 : -1) : 0)
}

function onDayClick(day) {
  if (props.pickMode !== 'none') {
    if (day.notPickable) return
    emit('pick', day.date)
    return
  }
  emit('select', day.date)
}

function toLocalDateStr(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function bgStyle(dateStr, hasEntries) {
  return dayBackground(dateStr, hasEntries, { dayStates: props.dayStates, showKcalColors: props.showKcalColors, kcalNorm: props.kcalNorm, dayKcals: props.dayKcals })
}

const monthLabel = computed(() => `${MONTHS[viewMonth.value]}${viewYear.value !== currentYear ? ` ${viewYear.value}` : ''}`)

const shownRange = computed(() => props.rangeFrom && props.rangeTo ? { from: props.rangeFrom, to: props.rangeTo } : null)

function monthDays(y, mo) {
  const daysInMonth = new Date(y, mo + 1, 0).getDate()
  const todayStr = toLocalDateStr(today)
  const r = shownRange.value
  const picking = props.pickMode !== 'none'
  const first = new Date(y, mo, 1).getDay()
  const days = []
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = toLocalDateStr(new Date(y, mo, d))
    const inBand = !!(r && dateStr >= r.from && dateStr <= r.to)
    days.push({
      date: dateStr,
      dayNum: d,
      hasEntries: props.daysWithEntries.includes(dateStr),
      isToday: dateStr === todayStr,
      isFuture: dateStr > todayStr,
      isPlanned: dateStr > todayStr && props.plannedDays.includes(dateStr),
      inBand,
      // Every visual row segment ends in a full rounded day square.
      bandStart: inBand && (dateStr === r.from || d === 1 || new Date(y, mo, d).getDay() === 1),
      bandEnd: inBand && (dateStr === r.to || d === daysInMonth || new Date(y, mo, d).getDay() === 0),
      isEdge: inBand && (dateStr === r.from || dateStr === r.to),
      isPickStart: picking && props.pickStart === dateStr,
      notPickable: picking && !!props.maxDay && dateStr > props.maxDay,
    })
  }
  return { key: `${y}-${mo}`, offset: first === 0 ? 6 : first - 1, days }
}

// Три месяца ленты: соседние нужны только на время жеста
const strip = computed(() => {
  const y = viewYear.value, mo = viewMonth.value
  const prev = mo === 0 ? [y - 1, 11] : [y, mo - 1]
  const next = mo === 11 ? [y + 1, 0] : [y, mo + 1]
  return [monthDays(...prev), monthDays(y, mo), monthDays(...next)]
})
</script>

<style scoped>
.cell { position: relative; height: var(--day-size); align-self: start; -webkit-touch-callout: none; }
/* Fill the entire square, including empty endpoints; coloured days sit above. */
.cell.in-band::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: -3px;   /* половина зазора: полосы соседних дней смыкаются */
  right: -3px;
  background: rgba(65, 41, 26, 0.12);
}
.cell.band-start::before { left: 0; border-top-left-radius: 12px; border-bottom-left-radius: 12px; }
.cell.band-end::before { right: 0; border-top-right-radius: 12px; border-bottom-right-radius: 12px; }
.cell > span { position: relative; }
.today-label { position:absolute; top:calc(100% + 2px); left:50%; transform:translateX(-50%); font-family:Inter,sans-serif; font-size:calc(var(--fs-13) * .8); line-height:1; font-weight:400; color:var(--ink-70); white-space:nowrap; }
.calendar-legend { display:flex; align-items:center; gap:6px 10px; margin-top:16px; font-family:Inter,sans-serif; font-size:var(--fs-12); line-height:1.4; color:var(--ink-70); }
.calendar-legend li { display:inline-flex; align-items:center; gap:5px; white-space:nowrap; }
.calendar-legend i { display:inline-block; flex:none; width:12px; height:12px; border-radius:4px; }
.incomplete-swatch { background-color:var(--day-incomplete-bg); background-image:repeating-linear-gradient(135deg,transparent 0 4px,var(--day-incomplete-line) 4px 5.5px,transparent 5.5px 7px); }
@media(max-width:380px){.calendar-legend{gap:6px}.calendar-legend li{gap:4px}.legend-day-word{display:none}}
/* Крайние дни периода и выбранное начало */
.day-edge { box-shadow: inset 0 0 0 1px var(--ink); font-weight: 600 !important; }
/* Запланированный день: пунктирная рамка цвета текста, без заливки */
.day-planned { outline: 1.5px dashed rgba(65, 41, 26, 0.55); outline-offset: -1.5px; }
</style>
